import { kanaeleZuordnen, type RohKanal } from "@kmvs/forms-core";
import { normalizePhone } from "../components/forms/validation/phone";
import { rechtsformAusName, type LegalFormOption } from "./legalFormOptions";
import { uid, type ContactRef } from "../components/relationsInline/types";
import type {
  VerantRole,
  VerantwortlichkeitValue,
} from "../components/verantwortlichkeit/VerantwortlichkeitInput";

/**
 * Übersetzt einen Visitenkarten-Befund in die Feldschlüssel der Anlage-Masken.
 *
 * Bewusst NICHT in `contactToFormState.ts` einsortiert: dort wird ein
 * bestehender Kontakt aus der API übersetzt, hier ein OCR-Befund — andere
 * Quelle, andere Regeln, keine gemeinsamen Helfer. Die Schlüssel sind die
 * BINDING-Keys der Masken (`postalcode` klein, nicht `postalCode`), sonst
 * landet der Wert im State, aber nie im Feld.
 *
 * Katalog-gebundene Felder (Anrede, Titel, Rechtsform) werden nur gesetzt, wenn
 * der Wert im jeweiligen Katalog vorkommt. Der Server löst diese Felder über
 * einen exakten Namensvergleich auf und verwirft einen Nichttreffer
 * kommentarlos — ein „Dipl.-Ing." von der Karte würde also spurlos
 * verschwinden. Was hier durchfällt, kommt deshalb als `uebergangen` zurück und
 * wird dem Anwender im Dialog angezeigt, statt still zu verschwinden.
 */

export type VisitenkarteBefund = Partial<{
  anredeText: string;
  titel: string;
  vorname: string;
  nachname: string;
  position: string;
  firma: string;
  rechtsform: string;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  /** Durchwahl der PERSON (seit 09/2026 getrennt von der Zentrale). */
  telefon: string;
  /** Handy der Person — gehört nie der Firma (V5). */
  mobil: string;
  /** Fax der Firma. */
  fax: string;
  /** Persönliche E-Mail der Person (V6). */
  email: string;
  /** Zentrale der Firma: „-0", „Tel. Zentrale" oder abgeleitet (V3/V4). */
  telefonZentrale: string;
  /** Sammeladresse der Firma: info@, kontakt@, office@ … (V3). */
  emailZentrale: string;
  /** „ja", wenn `telefonZentrale` aus der Durchwahl erschlossen ist (V4). */
  zentraleAbgeleitet: string;
  website: string;
  linkedin: string;
  xing: string;
  ustIdNr: string;
  handelsregister: string;
  registergericht: string;
  // Nur aus der vCard (T-356) — die Visitenkarten-Erkennung setzt sie nie.
  /** Private Festnetznummer (`TEL;TYPE=HOME` oder Karte ohne Firma). */
  telefonPrivat: string;
  /** Privates Handy. */
  mobilPrivat: string;
  /** Private E-Mail. */
  emailPrivat: string;
  /** Geburtstag als `JJJJ-MM-TT` (`BDAY`). */
  geburtsdatum: string;
}>;

/**
 * Ordnet die Kanäle eines Befunds Firma bzw. Person zu (V3–V6) und leitet die
 * Zentrale ab (V4) — mit denselben Regeln wie der Server
 * (`kanaeleZuordnen` aus forms-core).
 *
 * Nötig für die vCard, die im Browser gelesen wird und nur die Einzelfelder
 * kennt. Für einen Server-Befund ändert sich nichts: Er ist schon geordnet,
 * und die Regeln sind auf ihm stabil.
 */
export const visitenkarteKanaeleOrdnen = (befund: VisitenkarteBefund): VisitenkarteBefund => {
  const roh: RohKanal[] = [];
  const einzeln: Array<[keyof VisitenkarteBefund, string, "zentral" | "person" | undefined]> = [
    ["telefonZentrale", "telefon", "zentral"],
    ["emailZentrale", "email", "zentral"],
    ["telefon", "telefon", undefined],
    ["mobil", "mobil", "person"],
    ["fax", "fax", "zentral"],
    ["email", "email", undefined],
  ];
  for (const [feld, typ, bezug] of einzeln) {
    const wert = befund[feld];
    if (gesetzt(wert)) roh.push({ typ, wert, bezug });
  }
  const kanaele = kanaeleZuordnen(roh, { vorname: befund.vorname, nachname: befund.nachname });
  const aus: VisitenkarteBefund = { ...befund };
  for (const feld of ["telefon", "mobil", "fax", "email", "telefonZentrale", "emailZentrale", "zentraleAbgeleitet"] as const) {
    delete aus[feld];
  }
  const erstes = (typ: string, bezug?: string) =>
    kanaele.find((k) => k.typ === typ && (bezug === undefined || k.bezug === bezug));
  const zentrale = erstes("telefon", "zentral");
  if (zentrale) {
    aus.telefonZentrale = zentrale.wert;
    // Die Markierung des Servers bleibt, wenn er die Zentrale abgeleitet hat.
    if (zentrale.abgeleitet || (zentrale.wert === befund.telefonZentrale && befund.zentraleAbgeleitet === "ja")) {
      aus.zentraleAbgeleitet = "ja";
    }
  }
  const setze = (feld: keyof VisitenkarteBefund, k: { wert: string } | undefined) => {
    if (k) (aus as Record<string, string>)[feld] = k.wert;
  };
  setze("emailZentrale", erstes("email", "zentral"));
  setze("fax", erstes("fax"));
  setze("telefon", erstes("telefon", "person"));
  setze("mobil", erstes("mobil"));
  setze("email", erstes("email", "person"));
  return aus;
};

export type MapperKontext = {
  /** Anrede-Katalog (GET /contactsalutation), Werte wie „Herr", „Frau". */
  anredeOptionen?: string[];
  /** Titel-Auswahl der Maske — heute nur Dr. / Prof. / Prof. Dr. */
  titelOptionen?: string[];
  /** Rechtsform-Katalog; die IDs weichen zwischen DEV und PROD ab. */
  rechtsformOptionen?: LegalFormOption[];
};

/** Ein Wert, der bewusst nicht übernommen wurde — mit Begründung für den Dialog. */
export type UebergangenerWert = {
  feld: string;
  wert: string;
  grund: string;
};

export type MapperErgebnis = {
  werte: Record<string, string>;
  uebergangen: UebergangenerWert[];
};

const gesetzt = (wert: string | undefined): wert is string => !!wert && wert.trim() !== "";

/** Vergleich ohne Rücksicht auf Groß-/Kleinschreibung und Randleerzeichen. */
const findeImKatalog = (wert: string, katalog: string[] | undefined): string | undefined =>
  katalog?.find((eintrag) => eintrag.trim().toLowerCase() === wert.trim().toLowerCase());

/**
 * Rufnummern laufen durch `normalizePhone`, weil `PhoneInput` nur beim
 * Verlassen des Feldes normalisiert — ein per `set()` geschriebener Wert wird
 * dort nie angefasst und bliebe im Druckbild der Karte stehen.
 */
const alsRufnummer = (wert: string): string => normalizePhone(wert) || wert;

const adresse = (befund: VisitenkarteBefund, werte: Record<string, string>): void => {
  if (gesetzt(befund.strasse)) werte.street = befund.strasse;
  if (gesetzt(befund.plz)) werte.postalcode = befund.plz;
  if (gesetzt(befund.ort)) werte.city = befund.ort;
  if (gesetzt(befund.land)) werte.country = befund.land;
};

const kanaele = (
  befund: VisitenkarteBefund,
  werte: Record<string, string>,
  telefonSchluessel: { telefon: string; mobil: string; email: string },
): void => {
  if (gesetzt(befund.telefon)) werte[telefonSchluessel.telefon] = alsRufnummer(befund.telefon);
  if (gesetzt(befund.mobil)) werte[telefonSchluessel.mobil] = alsRufnummer(befund.mobil);
  if (gesetzt(befund.email)) werte[telefonSchluessel.email] = befund.email;

  if (gesetzt(befund.linkedin)) werte.linkedin = befund.linkedin;
  if (gesetzt(befund.xing)) werte.xing = befund.xing;
};

/** Visitenkarte → Personen-Maske (`NewPrivateContactForm`). */
export const visitenkarteZuPrivatFormState = (
  befund: VisitenkarteBefund,
  kontext: MapperKontext = {},
): MapperErgebnis => {
  const werte: Record<string, string> = {};
  const uebergangen: UebergangenerWert[] = [];

  if (gesetzt(befund.anredeText)) {
    const treffer = findeImKatalog(befund.anredeText, kontext.anredeOptionen);
    if (treffer) werte.anrede = treffer;
    else
      uebergangen.push({
        feld: "Anrede",
        wert: befund.anredeText,
        grund: "steht nicht im Anrede-Katalog",
      });
  }

  if (gesetzt(befund.titel)) {
    const treffer = findeImKatalog(befund.titel, kontext.titelOptionen);
    if (treffer) werte.title = treffer;
    else
      uebergangen.push({
        feld: "Titel",
        wert: befund.titel,
        grund: "steht nicht in der Auswahl der Maske",
      });
  }

  if (gesetzt(befund.vorname)) werte.firstname = befund.vorname;
  if (gesetzt(befund.nachname)) werte.lastname = befund.nachname;
  if (gesetzt(befund.position)) werte.position = befund.position;

  // Die Kanäle der PERSON (Durchwahl, Handy, persönliche E-Mail) sind
  // geschäftlich und tragen den Bezug zur Firma der Karte (P1/P8) — den setzt
  // die Maske (`visitenkarteFirmenbezug`), sobald die Firma feststeht.
  // Zentrale, Sammeladresse und Fax gehören der FIRMA (V3) und gehen mit der
  // Verbindung zum Unternehmen an sie, nicht an die Person.
  kanaele(befund, werte, {
    telefon: "phoneWork",
    mobil: "mobileWork",
    email: "emailWork",
  });
  const firmenkanaele: Array<[keyof VisitenkarteBefund, string]> = [
    ["telefonZentrale", "Telefon (Zentrale)"],
    ["emailZentrale", "E-Mail (Zentrale)"],
    ["fax", "Fax"],
  ];
  for (const [schluessel, label] of firmenkanaele) {
    const wert = befund[schluessel];
    if (gesetzt(wert)) {
      uebergangen.push({ feld: label, wert, grund: "Kanal der Firma — kommt mit der Verbindung an die Firma" });
    }
  }

  // Die Anschrift der Karte ist die der FIRMA, nicht die Wohnanschrift der
  // Person. Bis 09/2026 landete sie im Erstwohnsitz — die Person „wohnte"
  // danach im Büro. Sie geht jetzt an die Firma, wenn die angelegt wird.
  const firmenanschrift: Array<[keyof VisitenkarteBefund, string]> = [
    ["strasse", "Straße"],
    ["plz", "PLZ"],
    ["ort", "Ort"],
    ["land", "Land"],
  ];
  for (const [schluessel, label] of firmenanschrift) {
    const wert = befund[schluessel];
    if (gesetzt(wert)) {
      uebergangen.push({
        feld: label,
        wert,
        grund: "Firmenanschrift — gehört an die Firma, nicht an die Wohnanschrift",
      });
    }
  }

  // Website, USt-IdNr. und Handelsregister haben in der Personen-Maske
  // kein Zielfeld — sie gehören an den Firmendatensatz. Sichtbar machen statt
  // verschlucken, damit der Anwender weiß, dass die Karte mehr hergibt.
  const nurFirma: Array<[keyof VisitenkarteBefund, string]> = [
    ["website", "Website"],
    ["ustIdNr", "USt-IdNr."],
    ["handelsregister", "Handelsregister"],
    ["registergericht", "Registergericht"],
  ];
  for (const [schluessel, label] of nurFirma) {
    const wert = befund[schluessel];
    if (gesetzt(wert)) {
      uebergangen.push({
        feld: label,
        wert,
        grund: "gehört an die Firma, nicht an die Person",
      });
    }
  }

  if (gesetzt(befund.firma)) {
    uebergangen.push({
      feld: "Firma",
      wert: befund.firma,
      grund: "als Verbindung zur Firma anzulegen",
    });
  }

  return { werte, uebergangen };
};

const DATUM_DE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

/** Datum für das Datumsfeld der Maske — eine Korrektur darf „17.05.1980" sein. */
const alsIsoDatum = (wert: string): string => {
  const de = DATUM_DE.exec(wert.trim());
  return de ? `${de[3]}-${de[2].padStart(2, "0")}-${de[1].padStart(2, "0")}` : wert.trim();
};

/**
 * vCard → Personen-Maske (T-356).
 *
 * Mit Firma (`privat: false`) wie eine Visitenkarte: Kanäle geschäftlich,
 * Anschrift an die Firma. Zusätzlich gehen ausdrücklich private Kanäle
 * (`TYPE=HOME`) und der Geburtstag in die privaten Felder.
 *
 * Ohne Firma (`privat: true`) ist es ein privater Handykontakt: Kanäle privat,
 * Anschrift als Wohnanschrift, Geburtstag übernommen. Ob die Karte eine Firma
 * nennt, entscheidet der Aufrufer am GANZEN Befund — der Dialog fragt den
 * Mapper auch je Einzelfeld ab, dann fehlt die Firma im Teilbefund.
 */
export const vcardZuPrivatFormState = (
  befund: VisitenkarteBefund,
  kontext: MapperKontext = {},
  { privat }: { privat: boolean },
): MapperErgebnis => {
  const basis: VisitenkarteBefund = { ...befund };
  if (privat) {
    // Die Anschrift ist hier die Wohnanschrift, keine Firmenanschrift.
    delete basis.strasse;
    delete basis.plz;
    delete basis.ort;
    delete basis.land;
  }
  const ergebnis = visitenkarteZuPrivatFormState(basis, kontext);
  const werte = { ...ergebnis.werte };

  if (privat) adresse(befund, werte);
  if (gesetzt(befund.telefonPrivat)) werte.phonePrivate = alsRufnummer(befund.telefonPrivat);
  if (gesetzt(befund.mobilPrivat)) werte.mobilePrivate = alsRufnummer(befund.mobilPrivat);
  if (gesetzt(befund.emailPrivat)) werte.emailPrivate = befund.emailPrivat;
  if (gesetzt(befund.geburtsdatum)) werte.dateofbirth = alsIsoDatum(befund.geburtsdatum);

  return { werte, uebergangen: ergebnis.uebergangen };
};

/**
 * Handelsregister: die Maske trennt Präfix, Nummer und Gericht. Von der Karte
 * kommt meist „HRB 12345" in einem Stück.
 */
const registerZerlegen = (
  handelsregister: string | undefined,
): { register?: string; handelsregisterNummer?: string } => {
  if (!gesetzt(handelsregister)) return {};
  const treffer = /^(HRA|HRB|GnR|GsR|PR|VR)\s*([0-9]+.*)$/i.exec(handelsregister.trim());
  // Schreibweise des Registerportals: „GnR" und „GsR" (Gesellschaftsregister
  // der eGbR, T-329) — „GNR"/„GSR" kennt dessen Suchformular nicht.
  const art = treffer?.[1].toUpperCase().replace("GNR", "GnR").replace("GSR", "GsR");
  return treffer
    ? { register: art, handelsregisterNummer: treffer[2].trim() }
    : { handelsregisterNummer: handelsregister };
};

/** Visitenkarte → Firmen-Maske (`NewCompanyContactForm`). */
export const visitenkarteZuFirmaFormState = (
  befund: VisitenkarteBefund,
  kontext: MapperKontext = {},
): MapperErgebnis => {
  const werte: Record<string, string> = {};
  const uebergangen: UebergangenerWert[] = [];

  if (gesetzt(befund.firma)) {
    werte.name = befund.firma;

    // Die Rechtsform hängt in der Maske am Tippen im Namensfeld
    // (`applyLegalFormFromName` am onChange) — beim programmatischen Setzen
    // läuft sie nie. Deshalb hier selbst auflösen, über denselben Katalog:
    // die IDs unterscheiden sich zwischen DEV und PROD, sie dürfen nicht fest
    // verdrahtet werden.
    const { befund: rf, option } = rechtsformAusName(befund.firma, kontext.rechtsformOptionen ?? []);
    if (option && rf.konfidenz === "SICHER") {
      werte.gesellschaftsform = option.legalForm;
      werte.legalFormId = String(option.id);
    } else if (gesetzt(befund.rechtsform)) {
      uebergangen.push({
        feld: "Rechtsform",
        wert: befund.rechtsform,
        grund: option ? "nicht eindeutig — bitte selbst wählen" : "nicht im Katalog gefunden",
      });
    }
  }

  if (gesetzt(befund.website)) werte.website = befund.website;
  if (gesetzt(befund.fax)) werte.fax = alsRufnummer(befund.fax);
  if (gesetzt(befund.ustIdNr)) werte.umsatzsteuernummer = befund.ustIdNr;

  adresse(befund, werte);
  // An die Firma gehen nur die ZENTRALEN Kanäle (V1/V3). Durchwahl, Handy und
  // persönliche E-Mail sind die der Person (V2/V5/V6) — sie gehen mit dem
  // Eintrag unter „Verantwortung fürs Unternehmen" an die Person.
  if (gesetzt(befund.telefonZentrale)) werte.phone = alsRufnummer(befund.telefonZentrale);
  if (gesetzt(befund.emailZentrale)) werte.email = befund.emailZentrale;
  if (gesetzt(befund.linkedin)) werte.linkedin = befund.linkedin;
  if (gesetzt(befund.xing)) werte.xing = befund.xing;

  const register = registerZerlegen(befund.handelsregister);
  if (register.register) werte.register = register.register;
  if (register.handelsregisterNummer) werte.handelsregisterNummer = register.handelsregisterNummer;
  if (gesetzt(befund.registergericht)) werte.registergericht = befund.registergericht;

  // Person-Angaben haben in der Firmen-Maske kein Zielfeld.
  const nurPerson: Array<[keyof VisitenkarteBefund, string]> = [
    ["anredeText", "Anrede"],
    ["titel", "Titel"],
    ["vorname", "Vorname"],
    ["nachname", "Nachname"],
    ["position", "Position"],
    ["telefon", "Telefon (Durchwahl)"],
    ["mobil", "Mobil"],
    ["email", BEFUND_LABELS.email!],
    ["telefonPrivat", "Telefon (privat)"],
    ["mobilPrivat", "Mobil (privat)"],
    ["emailPrivat", "E-Mail (privat)"],
    ["geburtsdatum", "Geburtsdatum"],
  ];
  for (const [schluessel, label] of nurPerson) {
    const wert = befund[schluessel];
    if (gesetzt(wert)) {
      uebergangen.push({
        feld: label,
        wert,
        grund: "gehört an die Person, nicht an die Firma",
      });
    }
  }

  return { werte, uebergangen };
};

/**
 * Führt Vorder- und Rückseite zusammen.
 *
 * Die Vorderseite gewinnt bei jedem Feld, das sie belegt; die Rückseite füllt
 * nur Lücken. Weicht ein Feld auf beiden Seiten ab, wird der Konflikt gemeldet
 * statt still überschrieben — bei zwei verschiedenen Rufnummern ist stilles
 * Überschreiben der schlechteste Ausgang.
 *
 * Spiegelt `seitenZusammenfuehren` im Backend; hier nötig, weil die beiden
 * Seiten als getrennte Requests laufen (das nginx-Limit gilt pro Request) und
 * die Zusammenführung sonst einen dritten Aufruf bräuchte.
 */
export const seitenZusammenfuehren = (
  vorderseite: VisitenkarteBefund,
  rueckseite: VisitenkarteBefund = {},
): { felder: VisitenkarteBefund; konflikte: Array<{ feld: string; vorne: string; hinten: string }> } => {
  const felder: VisitenkarteBefund = { ...vorderseite };
  const konflikte: Array<{ feld: string; vorne: string; hinten: string }> = [];

  for (const [schluessel, hinten] of Object.entries(rueckseite)) {
    if (!gesetzt(hinten)) continue;
    const vorne = (vorderseite as Record<string, string | undefined>)[schluessel];

    if (!gesetzt(vorne)) {
      (felder as Record<string, string>)[schluessel] = hinten;
      continue;
    }
    if (vorne.toLowerCase() !== hinten.toLowerCase()) {
      konflikte.push({ feld: schluessel, vorne, hinten });
    }
  }

  return { felder, konflikte };
};

/** Anzeigenamen der Befund-Felder für die Prüfliste im Dialog. */
export const BEFUND_LABELS: Record<string, string> = {
  anredeText: "Anrede",
  titel: "Titel",
  vorname: "Vorname",
  nachname: "Nachname",
  position: "Position",
  firma: "Firma",
  rechtsform: "Rechtsform",
  strasse: "Straße",
  plz: "PLZ",
  ort: "Ort",
  land: "Land",
  telefon: "Telefon (Durchwahl)",
  mobil: "Mobil",
  fax: "Fax",
  // „Person", nicht „persönlich" (T-84): Die Adresse ist der geschäftliche
  // Kanal der Person bei ihrer Firma — „persönlich" las sich wie „privat".
  email: "E-Mail (Person)",
  telefonPrivat: "Telefon (privat)",
  mobilPrivat: "Mobil (privat)",
  emailPrivat: "E-Mail (privat)",
  geburtsdatum: "Geburtsdatum",
  telefonZentrale: "Telefon (Zentrale)",
  emailZentrale: "E-Mail (Zentrale)",
  website: "Website",
  linkedin: "LinkedIn",
  xing: "Xing",
  ustIdNr: "USt-IdNr.",
  handelsregister: "Handelsregister",
  registergericht: "Registergericht",
};

/** Reihenfolge der Felder in der Prüfliste. */
export const BEFUND_REIHENFOLGE: Array<keyof VisitenkarteBefund> = [
  "anredeText",
  "titel",
  "vorname",
  "nachname",
  "position",
  "geburtsdatum",
  "firma",
  "rechtsform",
  "strasse",
  "plz",
  "ort",
  "land",
  "telefon",
  "mobil",
  "email",
  "telefonPrivat",
  "mobilPrivat",
  "emailPrivat",
  "telefonZentrale",
  "emailZentrale",
  "fax",
  "website",
  "linkedin",
  "xing",
  "ustIdNr",
  "handelsregister",
  "registergericht",
];

// ─────────────────────────────────────────────────────────────────────────────
// Verknüpfung Person ↔ Firma
//
// Eine Visitenkarte nennt fast immer BEIDES: die Person und die Firma, für die
// sie arbeitet. Wer nur die Felder der jeweils offenen Maske füllt, verliert
// die Hälfte der Karte. Deshalb liefert der Scan zusätzlich eine
// Kontakt-Referenz für die jeweils andere Seite:
//
//   - Personen-Maske → die Firma wird als Arbeitgeber verknüpft
//     (`employerContactId`), im Bestand gesucht oder neu angelegt.
//   - Firmen-Maske   → die Person kommt als „Mensch im Unternehmen" in die
//     Verantwortlichkeiten (`rel_menschen`).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Die Person der Karte als Kontakt-Referenz.
 *
 * `manual: true` heißt: noch nicht im Bestand, wird ERST beim Speichern der
 * Firma angelegt (`persistCollectedRelations`). Damit bleibt es bei der Regel
 * der Inline-Verknüpfungen — während des Ausfüllens wird nichts geschrieben.
 */
export const visitenkarteZuPersonRef = (
  befund: VisitenkarteBefund,
  kontext: MapperKontext = {},
): ContactRef | null => {
  if (!gesetzt(befund.nachname) && !gesetzt(befund.vorname)) return null;

  const ref: ContactRef = {
    manual: true,
    type: "Person",
    firstName: befund.vorname,
    lastName: befund.nachname,
  };

  // Anrede und Titel nur, wenn der jeweilige Katalog sie kennt — der Server
  // löst sie über den Namen auf und verwirft einen Nichttreffer kommentarlos.
  if (gesetzt(befund.anredeText)) {
    const treffer = findeImKatalog(befund.anredeText, kontext.anredeOptionen);
    if (treffer) ref.salutationLabel = treffer;
  }
  if (gesetzt(befund.titel)) {
    const treffer = findeImKatalog(befund.titel, kontext.titelOptionen);
    if (treffer) ref.titleLabel = treffer;
  }

  // Die Kanäle einer Visitenkarte sind geschäftlich, nicht privat.
  if (gesetzt(befund.telefon)) ref.phoneWork = alsRufnummer(befund.telefon);
  if (gesetzt(befund.mobil)) ref.mobileWork = alsRufnummer(befund.mobil);
  if (gesetzt(befund.email)) ref.emailWork = befund.email;

  return ref;
};

/** Die Firma der Karte als Kontakt-Referenz (für die Arbeitgeber-Verknüpfung). */
export const visitenkarteZuFirmaRef = (
  befund: VisitenkarteBefund,
  kontext: MapperKontext = {},
): ContactRef | null => {
  if (!gesetzt(befund.firma)) return null;

  const ref: ContactRef = {
    manual: true,
    type: "Firma",
    lastName: befund.firma,
  };

  const { befund: rf, option } = rechtsformAusName(befund.firma, kontext.rechtsformOptionen ?? []);
  if (option && rf.konfidenz === "SICHER") {
    ref.legalForm = option.legalForm;
    ref.legalFormId = option.id;
  }

  // Nur die zentralen Kanäle (P4) — Handy und persönliche Adresse der Person
  // gehören nie der Firma (V5/V6).
  if (gesetzt(befund.telefonZentrale)) ref.phone = alsRufnummer(befund.telefonZentrale);
  if (gesetzt(befund.emailZentrale)) ref.email = befund.emailZentrale;
  if (gesetzt(befund.website)) ref.web = befund.website;
  if (gesetzt(befund.fax)) ref.fax = alsRufnummer(befund.fax);
  if (gesetzt(befund.ustIdNr)) ref.umsatzsteuernummer = befund.ustIdNr;
  const register = registerZerlegen(befund.handelsregister);
  if (register.register) ref.register = register.register;
  if (register.handelsregisterNummer) ref.handelsregisterNummer = register.handelsregisterNummer;
  if (gesetzt(befund.registergericht)) ref.registergericht = befund.registergericht;

  // Die Anschrift der Karte ist die Geschäftsanschrift der Firma — sie kommt
  // an die neu angelegte Firma, nicht an die Person.
  if (gesetzt(befund.strasse)) ref.street = befund.strasse;
  if (gesetzt(befund.plz)) ref.postalCode = befund.plz;
  if (gesetzt(befund.ort)) ref.city = befund.ort;
  if (gesetzt(befund.land)) ref.country = befund.land;

  return ref;
};

/**
 * Die Person der Karte als Eintrag für „Menschen fürs Unternehmen".
 *
 * Die Position wird bewusst als FREITEXT-Verantwortlichkeit gesetzt und NICHT
 * als Organrolle (`kind: "gf"`), selbst wenn auf der Karte „Geschäftsführer"
 * steht: An den Organrollen hängen Organigramm und Vertretungsbefugnis, und
 * „Geschäftsführer Vertrieb" ist gerade kein Organ. Was rechtlich gilt, muss
 * ein Mensch bestätigen — die Karte belegt nur, dass die Person dort in dieser
 * Funktion arbeitet.
 *
 * `istMenschImUnternehmen` ist gesetzt, weil genau das die Karte aussagt; es
 * ist zugleich der Weg, eine Person ohne Verantwortlichkeit zu erfassen
 * (Grundlage für bAV/bKV).
 */
export const visitenkarteZuMenschEntry = (
  befund: VisitenkarteBefund,
  kontext: MapperKontext = {},
): { id: string; ref: ContactRef; verant: VerantwortlichkeitValue } | null => {
  const ref = visitenkarteZuPersonRef(befund, kontext);
  if (!ref) return null;

  const roles: VerantRole[] = gesetzt(befund.position)
    ? [{ kind: "mensch", verantwortlichkeit: befund.position }]
    : [];

  return {
    id: uid(),
    ref,
    verant: {
      isExtern: false,
      roles,
      istMenschImUnternehmen: true,
    },
  };
};

type MenschEintrag = { id: string; ref: ContactRef | null; verant: VerantwortlichkeitValue | null };

const personName = (ref: ContactRef | null | undefined): string =>
  [ref?.firstName, ref?.lastName].filter(Boolean).join(" ").trim().toLowerCase();

/**
 * Die Person der Karte in „Verantwortung fürs Unternehmen" eintragen (V2) —
 * oder, wenn sie dort namentlich schon steht, ERGÄNZEN statt doppelt anlegen
 * (V8): leere Kanäle, Anrede und Titel auffüllen, die Position als Rolle
 * hinzufügen. Was schon dasteht, bleibt; an einem bestehenden Kontakt
 * (`contactId`) werden nur Rollen ergänzt — seine Stammdaten gehören der Akte.
 *
 * Liefert dieselbe Liste zurück, wenn es nichts zu tun gibt.
 */
export function menschEintragen<T extends MenschEintrag>(bisher: T[], neu: MenschEintrag): T[] {
  const name = personName(neu.ref);
  const i = name ? bisher.findIndex((m) => personName(m?.ref) === name) : -1;
  if (i < 0) return [...bisher, neu as T];

  const alt = bisher[i];
  let ref = alt.ref;
  if (ref && ref.manual && neu.ref) {
    const ergaenzt: ContactRef = { ...ref };
    for (const k of ["salutationLabel", "titleLabel", "phoneWork", "mobileWork", "emailWork"] as const) {
      if (!String(ergaenzt[k] ?? "").trim() && neu.ref[k]) ergaenzt[k] = neu.ref[k];
    }
    ref = ergaenzt;
  }
  const rollenAlt = alt.verant?.roles ?? [];
  const neueRollen = (neu.verant?.roles ?? []).filter(
    (r) =>
      !rollenAlt.some(
        (a) =>
          a.kind === r.kind &&
          String((a as { verantwortlichkeit?: string }).verantwortlichkeit ?? "").toLowerCase() ===
            String((r as { verantwortlichkeit?: string }).verantwortlichkeit ?? "").toLowerCase(),
      ),
  );
  const refGleich = JSON.stringify(ref) === JSON.stringify(alt.ref);
  if (refGleich && neueRollen.length === 0) return bisher;
  const verant: VerantwortlichkeitValue = {
    isExtern: false,
    ...(alt.verant ?? {}),
    roles: [...rollenAlt, ...neueRollen],
    istMenschImUnternehmen: true,
  };
  return bisher.map((m, idx) => (idx === i ? { ...m, ref, verant } : m));
}
